import { create } from 'zustand'
import { doc, updateDoc, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'

/**
 * Port of `src/stores/notification.js` (web path only).
 *
 * Push (`@capacitor-firebase/messaging`) only ever ran on native, where the
 * apps are now Swift / Kotlin. On web the legacy store already short-circuited
 * every push entry point on `!isNative`; those early returns are kept here as
 * the whole body. Firestore helpers (token save/remove, preferences) are
 * platform-independent and kept verbatim.
 */

export type NotificationResult = { success: boolean, error?: string, [key: string]: any }

export interface NotificationPreferences {
  checkInRemindersAll: boolean
  checkInRemindersRegular: boolean
  gameRemindersAll: boolean
  gameRemindersRegular: boolean
  waitlistPromotion: boolean
  gameAnnouncements: boolean
  [key: string]: any
}

export interface NotificationState {
  fcmToken: string | null
  permissionStatus: string // 'prompt', 'granted', 'denied'
  notifications: any[]
  isRegistered: boolean
  isInitialized: boolean
  preferences: NotificationPreferences
  // derived
  hasPermission: boolean
  // actions
  initialize: (userId?: string | null) => Promise<NotificationResult>
  initializeAndRegister: (userId?: string | null) => Promise<NotificationResult>
  requestPermissions: (userId?: string | null) => Promise<NotificationResult>
  saveTokenToFirestore: (userId: string, token: string) => Promise<NotificationResult>
  removeTokenFromFirestore: (userId: string, token?: string | null) => Promise<NotificationResult>
  retryPendingTokenSave: (userId: string) => Promise<NotificationResult>
  updatePreferences: (userId: string | null | undefined, newPreferences: Partial<NotificationPreferences>) => Promise<NotificationResult>
  loadPreferences: (userProfile: any) => void
  syncPermissionStatus: () => Promise<string>
  unregister: (userId?: string | null) => Promise<NotificationResult>
  clearNotifications: () => void
  getDeliveredNotifications: () => Promise<any[]>
  removeAllDeliveredNotifications: () => Promise<void>
}

let registrationInFlight: Promise<NotificationResult> | null = null // Dedupes concurrent initializeAndRegister() calls
let pendingTokenSave: { userId?: string, token?: string } | null = null // Store token if save fails, to retry later

export const useNotificationStore = create<NotificationState>()(
  derived<NotificationState>((s) => ({
    hasPermission: s.permissionStatus === 'granted'
  }))((set, get) => {
    /**
     * Initialize and register for push notifications.
     * Web: push is not available (legacy `!isNative` early return).
     */
    const runInitializeAndRegister = async (userId): Promise<NotificationResult> => {
      console.log('Push notifications only available on native platforms')
      return { success: true, message: 'Web platform - push not available' }
    }

    /**
     * Public entry point. Dedupes concurrent calls so the OS permission dialog can
     * never be requested twice.
     */
    const initializeAndRegister = async (userId) => {
      if (registrationInFlight) return registrationInFlight

      registrationInFlight = runInitializeAndRegister(userId).finally(() => {
        registrationInFlight = null
      })

      return registrationInFlight
    }

    /**
     * Legacy initialize method - now calls initializeAndRegister
     */
    const initialize = async (userId) => {
      return initializeAndRegister(userId)
    }

    /**
     * Request push notification permissions (called from settings).
     * Web: not available (legacy `!isNative` early return).
     */
    const requestPermissions = async (userId: string | null = null): Promise<NotificationResult> => {
      return { success: false, error: 'Not available on web' }
    }

    /**
     * Retry saving pending token (called when user becomes available)
     */
    const retryPendingTokenSave = async (userId) => {
      if (!pendingTokenSave) return { success: true }

      const tokenToSave = pendingTokenSave.token || get().fcmToken
      if (!tokenToSave || !userId) {
        console.log('No pending token or userId for retry')
        return { success: false, error: 'No token or userId' }
      }

      console.log('Retrying pending token save for user:', userId)
      const result = await saveTokenToFirestore(userId, tokenToSave)

      if (result.success) {
        pendingTokenSave = null
        console.log('Pending token save successful')
      }

      return result
    }

    /**
     * Save FCM token to user's Firestore document
     */
    const saveTokenToFirestore = async (userId, token): Promise<NotificationResult> => {
      if (!userId || !token) {
        console.error('saveTokenToFirestore: Missing userId or token')
        return { success: false, error: 'Missing userId or token' }
      }

      try {
        const userRef = doc(db, 'users', userId)

        // First get the current document to check existing tokens
        const userDoc = await getDoc(userRef)
        if (!userDoc.exists()) {
          console.error('User document not found:', userId)
          return { success: false, error: 'User not found' }
        }

        const userData = userDoc.data()
        const existingTokens = userData.fcmTokens || []

        // Check if this token already exists
        const tokenExists = existingTokens.some(t => t.token === token)

        if (tokenExists) {
          console.log('Token already exists for user, just enabling push')
          // Just update pushNotificationsEnabled
          await updateDoc(userRef, {
            pushNotificationsEnabled: true
          })
        } else {
          // Add new token
          console.log('Adding new token for user')
          const newToken = {
            token: token,
            // Legacy: Capacitor.getPlatform(), which is 'web' in a browser.
            platform: 'web',
            createdAt: new Date().toISOString()
          }

          await updateDoc(userRef, {
            fcmTokens: [...existingTokens, newToken],
            pushNotificationsEnabled: true
          })
        }

        console.log('Token saved successfully to Firestore')
        return { success: true }
      } catch (error) {
        console.error('Error saving FCM token:', error)
        return { success: false, error: error.message }
      }
    }

    /**
     * Remove FCM token from user's Firestore document
     */
    const removeTokenFromFirestore = async (userId, token) => {
      try {
        const userRef = doc(db, 'users', userId)
        // We need to find and remove the token object
        // For simplicity, we'll just disable push notifications
        await updateDoc(userRef, {
          pushNotificationsEnabled: false
        })
        return { success: true }
      } catch (error) {
        console.error('Error removing FCM token:', error)
        return { success: false, error: error.message }
      }
    }

    /**
     * Update notification preferences
     */
    const updatePreferences = async (userId, newPreferences) => {
      try {
        set({ preferences: { ...get().preferences, ...newPreferences } })

        if (userId) {
          const userRef = doc(db, 'users', userId)
          await updateDoc(userRef, {
            notificationPreferences: get().preferences
          })
        }

        return { success: true }
      } catch (error) {
        console.error('Error updating notification preferences:', error)
        return { success: false, error: error.message }
      }
    }

    /**
     * Load notification preferences from user profile
     *
     * Note: this deliberately does NOT touch permissionStatus. pushNotificationsEnabled
     * is an account-wide Firestore flag set by any device that ever registered, so
     * using it to infer permission makes a brand-new device report "granted" and hides
     * the Enable button, leaving the device permanently unregistered. Permission state
     * must come from the OS via syncPermissionStatus().
     */
    const loadPreferences = (userProfile) => {
      if (userProfile?.notificationPreferences) {
        set({ preferences: { ...get().preferences, ...userProfile.notificationPreferences } })
      }
    }

    /**
     * Read the real OS-level notification permission for THIS device.
     * Web: returns the stored status unchanged (legacy `!isNative` early return).
     */
    const syncPermissionStatus = async () => {
      return get().permissionStatus
    }

    /**
     * Unregister from push notifications.
     * Web: no-op (legacy `!isNative` early return).
     */
    const unregister = async (userId) => {
      return { success: true }
    }

    /**
     * Clear all notifications
     */
    const clearNotifications = () => {
      set({ notifications: [] })
    }

    /**
     * Get delivered notifications (iOS only). Web: always empty.
     */
    const getDeliveredNotifications = async () => {
      return []
    }

    /**
     * Remove all delivered notifications. Web: no-op.
     */
    const removeAllDeliveredNotifications = async () => {
    }

    return {
      fcmToken: null,
      permissionStatus: 'prompt',
      notifications: [],
      isRegistered: false,
      isInitialized: false,
      // Notification preferences (stored in user profile)
      preferences: {
        checkInRemindersAll: false, // Check-in opens for ALL games (default off)
        checkInRemindersRegular: true, // Check-in opens for games you're a regular for (default on)
        gameRemindersAll: false, // Game reminders for ALL games (default off)
        gameRemindersRegular: true, // Game reminders for games you're a regular for (default on)
        waitlistPromotion: true, // Notify when moved from waitlist to team
        gameAnnouncements: true // Notify when admin adds game notes
      },
      hasPermission: false,
      initialize,
      initializeAndRegister,
      requestPermissions,
      saveTokenToFirestore,
      removeTokenFromFirestore,
      retryPendingTokenSave,
      updatePreferences,
      loadPreferences,
      syncPermissionStatus,
      unregister,
      clearNotifications,
      getDeliveredNotifications,
      removeAllDeliveredNotifications
    }
  })
)
